import React, { useState, useEffect, useRef } from 'react';
import API from '../services/api';

const CRAYON_PALETTE = [
  '#CC0000','#FF3333','#FF6666','#FF9999',
  '#FF007F','#FF69B4','#FF1493','#FFB6C1',
  '#FF6600','#FF8C00','#FFA040','#FFB347',
  '#FFD700','#FFE066','#FFFF00','#FFFACD',
  '#9ACD32','#ADFF2F','#7FFF00','#32CD32',
  '#008000','#228B22','#00CC00','#00FA9A',
  '#20B2AA','#008B8B','#00CED1','#00FFFF',
  '#87CEEB','#00BFFF','#1E90FF','#4169E1',
  '#0000FF','#0000CD','#000080','#191970',
  '#8A2BE2','#9400D3','#9370DB','#8B008B',
  '#EE82EE','#DDA0DD','#DA70D6','#FF00FF',
  '#8B4513','#D2691E','#CD853F','#F4A460',
  '#000000','#555555','#AAAAAA','#FFFFFF',
];

function BirthdayCardModal({ recipientId, recipientFirstName, type, onClose }) {
  const [selectedColor, setSelectedColor] = useState(CRAYON_PALETTE[0]);
  const [brushSize, setBrushSize]         = useState(6);
  const [eraseMode, setEraseMode]         = useState(false);
  const [note, setNote]                   = useState('');
  const [sending, setSending]             = useState(false);
  const [sent, setSent]                   = useState(false);
  const [error, setError]                 = useState('');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [historyLength, setHistoryLength] = useState(0);
  const canvasRef      = useRef(null);
  const isDrawingRef   = useRef(false);
  const historyRef     = useRef([]);
  const closeTimerRef  = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fffef7';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    historyRef.current = [ctx.getImageData(0, 0, canvas.width, canvas.height)];
    setHistoryLength(1);
    return () => { if (closeTimerRef.current) clearTimeout(closeTimerRef.current); };
  }, []);

  function getPos(e) {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }

  function handlePointerDown(e) {
    e.preventDefault();
    isDrawingRef.current = true;
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
  }

  function handlePointerMove(e) {
    if (!isDrawingRef.current) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.strokeStyle = eraseMode ? '#fffef7' : selectedColor;
    ctx.lineWidth = brushSize;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
  }

  function handlePointerUp() {
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const snapshot = canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height);
    historyRef.current.push(snapshot);
    setHistoryLength(historyRef.current.length);
  }

  function handleUndo() {
    if (historyRef.current.length <= 1) return;
    historyRef.current.pop();
    const prev = historyRef.current[historyRef.current.length - 1];
    canvasRef.current.getContext('2d').putImageData(prev, 0, 0);
    setHistoryLength(historyRef.current.length);
  }

  async function handleSend() {
    setSending(true);
    setError('');
    try {
      const dataUrl = canvasRef.current.toDataURL('image/png');
      const imageData = dataUrl.split(',')[1];
      await API.post('/therapist/birthday-card', {
        recipientId,
        type,
        imageData,
        note: note.trim(),
      });
      setSent(true);
      closeTimerRef.current = setTimeout(onClose, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send card. Please try again.');
    } finally {
      setSending(false);
    }
  }

  const isBirthday = type === 'birthday';
  const emoji = isBirthday ? '🎂' : '🎉';

  const toolBtn = (active, disabled) => ({
    padding: '4px 14px', borderRadius: 6, fontSize: 13,
    cursor: disabled ? 'not-allowed' : 'pointer',
    background: active ? '#1D2C5F' : '#f3f4f6',
    color: active ? '#fff' : '#374151',
    border: '1px solid ' + (active ? '#1D2C5F' : '#e5e7eb'),
    fontWeight: active ? 700 : 400,
    opacity: disabled ? 0.4 : 1,
  });

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff', borderRadius: 16, padding: 24, width: '100%',
          maxWidth: 540, boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {sent ? (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>🎉</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#1D2C5F' }}>
              Sent! {recipientFirstName} will love it 🎉
            </div>
          </div>
        ) : (
          <>
            <h3 style={{ margin: '0 0 16px', color: '#1D2C5F' }}>
              {emoji} Draw a card for {recipientFirstName}
            </h3>

            {/* Color picker trigger */}
            <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                onClick={() => setShowColorPicker(v => !v)}
                style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: selectedColor, border: '3px solid #1D2C5F',
                  cursor: 'pointer', flexShrink: 0,
                }}
              />
              <span
                style={{ fontSize: 11, color: '#6B7280', cursor: 'pointer' }}
                onClick={() => setShowColorPicker(v => !v)}
              >
                ▼ Color
              </span>
            </div>

            {/* Color picker overlay */}
            {showColorPicker && (
              <div
                style={{
                  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                  zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: 16,
                }}
                onClick={() => setShowColorPicker(false)}
              >
                <div
                  style={{
                    background: '#fff', borderRadius: 12, padding: 16,
                    width: '100%', maxWidth: 320,
                  }}
                  onClick={e => e.stopPropagation()}
                >
                  <div style={{ fontWeight: 700, marginBottom: 12, color: '#1D2C5F' }}>
                    Pick a color
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 8 }}>
                    {CRAYON_PALETTE.map(c => (
                      <button
                        key={c}
                        onClick={() => { setSelectedColor(c); setShowColorPicker(false); }}
                        style={{
                          width: '100%', aspectRatio: '1', borderRadius: 6,
                          background: c,
                          border: c === selectedColor ? '3px solid #1D2C5F' : '1px solid #e5e7eb',
                          cursor: 'pointer',
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Tool row: brush size + erase + undo */}
            <div style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {[{ label: 'S', size: 3 }, { label: 'M', size: 6 }, { label: 'L', size: 12 }].map(({ label, size }) => (
                <button
                  key={size}
                  onClick={() => setBrushSize(size)}
                  style={toolBtn(brushSize === size, false)}
                >
                  {label}
                </button>
              ))}
              <button onClick={() => setEraseMode(v => !v)} style={toolBtn(eraseMode, false)}>
                🧹 Erase
              </button>
              <button
                onClick={handleUndo}
                disabled={historyLength <= 1}
                style={toolBtn(false, historyLength <= 1)}
              >
                ↩ Undo
              </button>
            </div>

            {/* Canvas */}
            <canvas
              ref={canvasRef}
              width={480}
              height={260}
              style={{
                width: '100%', aspectRatio: '480/260', borderRadius: 8,
                border: '1px solid #e5e7eb', background: '#fffef7',
                display: 'block', touchAction: 'none',
                cursor: eraseMode ? 'cell' : 'crosshair',
              }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerUp}
            />

            {/* Note */}
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="Add a personal note… (optional)"
              style={{
                width: '100%', marginTop: 12, padding: '8px 12px', borderRadius: 8,
                border: '1px solid #e5e7eb', fontSize: 14, resize: 'none',
                fontFamily: 'inherit', boxSizing: 'border-box',
              }}
            />

            {error && (
              <div style={{ color: '#DC2626', fontSize: 13, marginTop: 6 }}>{error}</div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button
                onClick={handleSend}
                disabled={sending}
                style={{
                  flex: 1, padding: '10px 0', borderRadius: 8, fontSize: 15, fontWeight: 700,
                  background: sending ? '#9ca3af' : '#1D2C5F', color: '#fff',
                  border: 'none', cursor: sending ? 'not-allowed' : 'pointer',
                }}
              >
                {sending ? 'Sending…' : 'Send Card'}
              </button>
              <button
                onClick={onClose}
                style={{
                  flex: 1, padding: '10px 0', borderRadius: 8, fontSize: 15,
                  background: '#f3f4f6', color: '#374151',
                  border: '1px solid #e5e7eb', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default BirthdayCardModal;
